import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { sidebarQueries } from "../queries/sidebar";
import { auth } from "../queries/me";
import { denormalizeFolders } from "../util/sidebarNormalizer";
import { Folder } from "../util/Types/Folder";
import { Space } from "../util/Types/Sidebar";

const updateFlowsInFolderTree = (
  folder: Folder,
  flowId: string,
  updates: Partial<{ name: string }>,
): Folder => {
  const updatedFlows = folder.flows?.map((flow) =>
    flow.id === flowId ? { ...flow, ...updates } : flow,
  );
  const updatedSubfolders = folder.folders?.map((sub) =>
    updateFlowsInFolderTree(sub, flowId, updates),
  );
  return { ...folder, flows: updatedFlows || [], folders: updatedSubfolders || [] };
};

const removeFlowFromFolderTree = (
  folder: Folder,
  flowId: string,
): Folder => {
  const filteredFlows = folder.flows?.filter((flow) => flow.id !== flowId) || [];
  const updatedSubfolders = folder.folders?.map((sub) =>
    removeFlowFromFolderTree(sub, flowId),
  );
  return { ...folder, flows: filteredFlows, folders: updatedSubfolders || [] };
};

export const useSidebarData = () => {
  const queryClient = useQueryClient();

  const {
    data: foldersData,
    isLoading: foldersLoading,
    error: foldersError,
  } = useQuery(sidebarQueries.folders());

  const {
    data: spacesData,
    isLoading: spacesLoading,
    error: spacesError,
  } = useQuery(sidebarQueries.spaces());

  const { data: userData } = useQuery(auth.me());

  const folders: Folder[] = useMemo(
    () =>
      foldersData
        ? denormalizeFolders(foldersData.foldersMap, foldersData.folderHierarchy)
        : [],
    [foldersData],
  );

  const spaces: Space[] = useMemo(
    () => (spacesData ? Object.values(spacesData) : []),
    [spacesData],
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["sidebar"] });
    queryClient.invalidateQueries({ queryKey: ["me"] });
  }, [queryClient]);

  const updateFlow = useCallback(
    (flowId: string, updates: Partial<{ name: string }>) => {
      queryClient.setQueryData(sidebarQueries.folders().queryKey, (old: any) => {
        if (!old) return old;
        const updated = { ...old };
        if (updated.flowsMap && updated.flowsMap[flowId]) {
          updated.flowsMap[flowId] = {
            ...updated.flowsMap[flowId],
            ...updates,
          };
          const folderId = updated.flowToFolder[flowId];
          if (folderId && updated.foldersMap[folderId]) {
            const folder = { ...updated.foldersMap[folderId] };
            folder.flows = folder.flows.map((flow: any) =>
              flow.id === flowId ? { ...flow, ...updates } : flow,
            );
            updated.foldersMap[folderId] = folder;
          }
        }
        return updated;
      });

      queryClient.setQueryData(sidebarQueries.spaces().queryKey, (old: any) => {
        if (!old) return old;
        const updated = { ...old };
        Object.keys(updated).forEach((spaceId) => {
          const space = { ...updated[spaceId] };
          space.folder = space.folder.map((folder: Folder) =>
            updateFlowsInFolderTree(folder, flowId, updates),
          );
          space.flows = space.flows.map((flow: any) =>
            flow.id === flowId ? { ...flow, ...updates } : flow,
          );
          updated[spaceId] = space;
        });
        return updated;
      });
    },
    [queryClient],
  );

  const removeFlow = useCallback(
    (flowId: string) => {
      queryClient.setQueryData(sidebarQueries.folders().queryKey, (old: any) => {
        if (!old) return old;
        const updated = { ...old };
        const folderId = updated.flowToFolder[flowId];
        if (folderId && updated.foldersMap[folderId]) {
          const folder = { ...updated.foldersMap[folderId] };
          folder.flows = folder.flows.filter((flow: any) => flow.id !== flowId);
          updated.foldersMap[folderId] = folder;
        }
        delete updated.flowsMap[flowId];
        delete updated.flowToFolder[flowId];
        return updated;
      });

      queryClient.setQueryData(sidebarQueries.spaces().queryKey, (old: any) => {
        if (!old) return old;
        const updated = { ...old };
        Object.keys(updated).forEach((spaceId) => {
          const space = { ...updated[spaceId] };
          space.folder = space.folder.map((folder: Folder) =>
            removeFlowFromFolderTree(folder, flowId),
          );
          space.flows = space.flows.filter((flow: any) => flow.id !== flowId);
          updated[spaceId] = space;
        });
        return updated;
      });
    },
    [queryClient],
  );

  const updateSpace = useCallback(
    (spaceId: string, updater: (space: Space) => Space) => {
      queryClient.setQueryData(sidebarQueries.spaces().queryKey, (old: any) => {
        if (!old || !old[spaceId]) return old;
        const updated = { ...old };
        updated[spaceId] = updater(old[spaceId]);
        return updated;
      });
    },
    [queryClient],
  );

  return {
    folders,
    spaces,
    user: userData as any,
    isLoading: foldersLoading || spacesLoading,
    error: foldersError || spacesError,
    refresh,
    updateFlow,
    removeFlow,
    updateSpace,
    normalizedFolders: foldersData,
    normalizedSpaces: spacesData,
  };
};
